import React from "react";
import { Search } from "lucide-react";

import { CompactButton, PrimaryButton, SecondaryButton } from "../Button/Buttons";
import AlertBanner from "../shared/AlertBanner/AlertBanner";
import InlineSpinner from "../shared/Spinner/InlineSpinner";

const AgentToolDiscoveryPanel = ({
  canDiscover,
  isDiscovering,
  discoveryProgressLabel,
  onDiscover,
  error,
  tools,
  filteredTools,
  selectedNames,
  search,
  onSearchChange,
  onToggleTool,
  onToggleAll,
  allFilteredSelected,
  someFilteredSelected,
  onCancel,
  onGenerate,
  isGenerating,
  canRegeneratePrompt,
  regeneratePrompt,
  onRegeneratePromptChange,
  result,
}) => (
  <div className="mt-4 rounded-lg border border-edge-gray-200 dark:border-edge-gray-700 bg-fill-gray-50 dark:bg-fill-deep-surface/40 p-4">
    <CompactButton
      type="button"
      onClick={onDiscover}
      disabled={!canDiscover || isDiscovering}
      className="inline-flex items-center gap-2 disabled:cursor-not-allowed disabled:opacity-50"
    >
      {isDiscovering ? <InlineSpinner size="xs" /> : <Search className="h-3.5 w-3.5" />}
      {isDiscovering ? discoveryProgressLabel : "Discover Tools"}
    </CompactButton>
    {error && <AlertBanner className="mt-3">{error}</AlertBanner>}
    {result && (
      <p role="status" aria-live="polite" className="mt-3 text-xs text-ink-gray-600 dark:text-ink-gray-300">
        {result}
      </p>
    )}
    {tools && (
      <div className="mt-4 border-t border-edge-gray-200 dark:border-edge-gray-700 pt-4">
        <div className="flex items-center justify-between gap-4 mb-3">
          <div>
            <h4 className="text-sm font-semibold text-ink-gray-900 dark:text-ink-white">
              Discovered tools
            </h4>
            <p className="text-[11px] text-ink-gray-400 mt-1">Select the tools this agent may use.</p>
          </div>
          <span className="text-xs text-ink-gray-500 whitespace-nowrap">
            {selectedNames.length} selected · {tools.length} found
          </span>
        </div>
        {tools.length === 0 ? (
          <p className="text-sm text-ink-gray-500">This MCP server did not report any tools.</p>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-3 rounded-lg border border-edge-gray-200 dark:border-edge-gray-700 bg-fill-white dark:bg-fill-panel-surface px-3 py-2">
              <Search className="h-3.5 w-3.5 text-ink-gray-400 flex-shrink-0" />
              <input
                type="text"
                value={search}
                onChange={(event) => onSearchChange(event.target.value)}
                onKeyDown={(event) => { if (event.key === "Enter") event.preventDefault(); }}
                placeholder="Filter tools by name or description…"
                className="flex-1 bg-transparent text-sm outline-none text-ink-ink dark:text-ink-white placeholder-ink-gray-400"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => onSearchChange("")}
                  className="text-xs text-ink-gray-400 hover:text-ink-gray-600 dark:hover:text-ink-gray-200"
                >
                  Clear
                </button>
              )}
            </div>
            {filteredTools.length === 0 ? (
              <p className="text-sm text-ink-gray-500 py-3">No tools match "{search}"</p>
            ) : (
              <div className="rounded-lg border border-edge-gray-200 dark:border-edge-gray-700 bg-fill-white dark:bg-fill-panel-surface">
                <label className="flex items-center gap-3 px-3 py-2 border-b border-edge-gray-200 dark:border-edge-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={allFilteredSelected}
                    ref={(element) => { if (element) element.indeterminate = someFilteredSelected && !allFilteredSelected; }}
                    onChange={onToggleAll}
                    className="h-4 w-4"
                  />
                  <span className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">
                    {search.trim() ? `Select all ${filteredTools.length} matching` : "Select all"}
                  </span>
                </label>
                <div className="max-h-64 overflow-y-auto divide-y divide-edge-gray-200 dark:divide-edge-gray-700">
                  {filteredTools.map((tool) => (
                    <label key={tool.name} className="flex items-start gap-3 px-3 py-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedNames.includes(tool.name)}
                        onChange={(event) => onToggleTool(tool.name, event.target.checked)}
                        className="mt-1 h-4 w-4"
                      />
                      <span className="min-w-0">
                        <span className="flex items-center gap-2 text-sm font-medium text-ink-gray-900 dark:text-ink-white">
                          <span>{tool.name}</span>
                          {tool.governance?.classification_status === "fallback" && (
                            <span className="text-[11px] font-medium text-ink-amber-700 dark:text-ink-amber-300">
                              Fallback classification
                            </span>
                          )}
                        </span>
                        <span className="block text-xs text-ink-gray-500 dark:text-ink-gray-400">
                          {tool.description || "No description provided"}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
        {canRegeneratePrompt && (
          <label className="mt-4 flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={regeneratePrompt}
              onChange={(event) => onRegeneratePromptChange(event.target.checked)}
              className="mt-0.5 h-4 w-4"
            />
            <span className="text-xs text-ink-gray-700 dark:text-ink-gray-300">
              Regenerate prompt from selected tools
              <span className="block text-[11px] text-ink-gray-400">
                Replaces the current prompt draft. Leave unchecked to keep it.
              </span>
            </span>
          </label>
        )}
        <div className="mt-4 flex justify-end gap-3">
          <SecondaryButton type="button" onClick={onCancel}>Close</SecondaryButton>
          <PrimaryButton
            type="button"
            onClick={onGenerate}
            disabled={selectedNames.length === 0}
            loading={isGenerating}
            loadingLabel="Generating"
          >
            {result ? "Regenerate" : "Generate"} Policy, Tools &amp; Prompt
          </PrimaryButton>
        </div>
      </div>
    )}
  </div>
);

export default AgentToolDiscoveryPanel;
